/**
 * SHELL — runtime configuration & environment.
 *
 * Centralizes build metadata, platform detection, and the public Supabase config
 * read from Vite env. No secrets here: only the anon key + URL, which are safe to
 * ship (RLS is the real guard). See `supabase/README.md`.
 */

import { PROTOCOL_VERSION } from '@/types';

/** Human-readable build version. Overridable at build time via Vite define. */
export const CLIENT_VERSION: string =
  (import.meta.env.VITE_CLIENT_VERSION as string | undefined) ?? '0.1.0-dev';

export const PROTOCOL = PROTOCOL_VERSION;

/** Coarse platform label echoed in ClientMetadata.platform. */
export function detectPlatform(): string {
  // Capacitor injects a global when running inside the native shell.
  const cap = (globalThis as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  const native = cap?.getPlatform?.();
  if (native === 'ios') return 'ios-native';
  if (native === 'android') return 'android-native';

  const standalone = isStandaloneDisplay();
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return standalone ? 'ios-pwa' : 'web-ios';
  if (/Android/.test(ua)) return standalone ? 'android-pwa' : 'web-android';
  return standalone ? 'web-pwa' : 'web';
}

/** True when launched as an installed PWA (display-mode: standalone). */
export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  const mql = window.matchMedia?.('(display-mode: standalone)');
  const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return Boolean(mql?.matches) || iosStandalone;
}

/** Running inside the Capacitor native wrapper. */
export function isNativeApp(): boolean {
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return cap?.isNativePlatform?.() === true;
}

/** Public Supabase configuration from env. Either may be empty in local dev. */
export interface SupabaseConfig {
  readonly url: string;
  readonly anonKey: string;
}

export function readSupabaseConfig(): SupabaseConfig {
  return {
    url: (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '',
    anonKey: (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '',
  };
}

/** True when we have enough config to attempt a real Supabase session. */
export function hasSupabase(cfg: SupabaseConfig = readSupabaseConfig()): boolean {
  return cfg.url.length > 0 && cfg.anonKey.length > 0;
}

/** Base URL for the Backend Pages Functions. Same origin in production. */
export const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? '';
